import { useState, useEffect } from "react"
import { formatEther } from "viem"
import { useChainId } from "wagmi"
import { publicClients } from "../lib/wagmi"
import { childPool } from "../utils/contract"

export const useTotalRootPoolsTVL = (rootPools) => {
    const [totalTVL, setTotalTVL] = useState(null)
    const [totalTVLUSD, setTotalTVLUSD] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const chainId = useChainId()

    // Map chain IDs to CoinGecko platform names
    const chainToPlatform = {
        1: 'ethereum',
       
    }

    // Native token addresses (0x0 or null means native token)
    const nativeTokenIds = {
        1: 'ethereum',
    }

    const fetchTokenPrice = async (tokenAddress) => {
        try {
            const normalizedAddress = tokenAddress?.toLowerCase() || 'native'
            const platform = chainToPlatform[chainId]

            if (!platform) {
                throw new Error(`Unsupported chain ID: ${chainId}`)
            }

            // Check if it's a native token
            const isNativeToken = !tokenAddress ||
                tokenAddress === '0x0' ||
                tokenAddress === '0x0000000000000000000000000000000000000000'

            let response

            if (isNativeToken) {
                const tokenId = nativeTokenIds[chainId]
                response = await fetch(
                    `https://pro-api.coingecko.com/api/v3/simple/price?ids=${tokenId}&vs_currencies=usd`,
                    {
                        headers: {
                            'x-cg-pro-api-key': process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6ozdfd8K7Qpx7qESamW9hKZu',
                        },
                    }
                )

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

                const data = await response.json()
                return data[tokenId]?.usd
            } else {
                response = await fetch(
                    `https://pro-api.coingecko.com/api/v3/simple/token_price/${platform}?contract_addresses=${normalizedAddress}&vs_currencies=usd`,
                    {
                        headers: {
                            'x-cg-pro-api-key': process.env.REACT_APP_COINGECKO_API_KEY || 'CG-AMT6ozdfd8K7Qpx7qESamW9hKZu',
                        },
                    }
                )

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

                const data = await response.json()
                return data[normalizedAddress]?.usd
            }
        } catch (err) {
            console.error('Error fetching token price:', err)
            return null
        }
    }

    useEffect(() => {
        if (!rootPools || rootPools.length === 0) {
            setTotalTVL(0)
            setTotalTVLUSD(0)
            setLoading(false)
            return
        }

        const fetchAllTVLs = async () => {
            try {
                setLoading(true)
                setError(null)

                let totalTokens = 0
                let totalUSD = 0

                // Fetch TVL for each pool
                for (const pool of rootPools) {
                    try {
                        // Fetch TVL from contract
                        const result = await publicClients[chainId].readContract({
                            address: pool.contractAddress,
                            abi: childPool.abi,
                            functionName: "tvl",
                            args: [],
                        })

                        const tvlValue = parseFloat(formatEther(result))
                        totalTokens += tvlValue

                        // Fetch token price and calculate USD value
                        const price = await fetchTokenPrice(pool.rewardToken)
                        if (price) {
                            totalUSD += tvlValue * price
                        }
                    } catch (poolError) {
                        console.error(`Error fetching TVL for pool ${pool.contractAddress}:`, poolError)
                    }
                }

                setTotalTVL(totalTokens)
                setTotalTVLUSD(totalUSD)
                setLoading(false)
            } catch (err) {
                console.error("Error fetching total TVL:", err)
                setError(err)
                setTotalTVL(0)
                setTotalTVLUSD(0)
                setLoading(false)
            }
        }

        fetchAllTVLs()
    }, [rootPools, chainId])

    return { totalTVL, totalTVLUSD, loading, error }
}